import React, { useReducer, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import StockOpnameTable from "../../elements/Tables/StockOpnameTable/StockOpnameTable";
import { useFetch } from "../../../Utils/useFetch";
import {
  FETCH_ALL_STOCKOPNAME_API,
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_DEPT_API,
} from "../../../Constants/Apis";
import { Input, Checkbox, Select, Button, message } from "antd";
import { rejectStockOpname } from "../../../Services/StockOpnameService";
import { reducer } from "../../../Utils/CommonUtils";
import { updateStockQuantity } from "../../../Services/StockService";

const { Search } = Input;
const { Option } = Select;

const StockOpnamePage = ({ history, userData }) => {
  // --- seed initial state with ECOMMRI BEFORE first render ---
  const isEcomm = userData?.counterCode === "ECOMMRI";

  const initialBase = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
    showNotMatched: false,
    counterCode: isEcomm ? "ECOMMRI" : null, // <= seeded
  };

  // lazy initializer to avoid double-init when userData is stable
  const [state, dispatch] = useReducer(reducer, initialBase, (s) => s);

  const [selectedCounterCode, setSelectedCounterCode] = useState(
    isEcomm ? "ECOMMRI" : null // <= seeded
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [rejecting, setRejecting] = useState(false);

  const stockOpnameApiUrl = `${FETCH_ALL_STOCKOPNAME_API}/agg`;

  // Fetch data with already-correct state (no “null” counterCode round-trip)
  const { fetchedData, callReFetch, loading } = useFetch(
    stockOpnameApiUrl,
    state
  );

  // Auto-refetch when query state changes (guard: for ECOMMRI, ensure state is pinned)
  useEffect(() => {
    if (isEcomm && state.counterCode !== "ECOMMRI") return; // safety guard
    callReFetch();
  }, [
    isEcomm,
    state.searchKeyword,
    state.current,
    state.pageSize,
    state.showNotMatched,
    state.counterCode,
  ]);

  const [localTableData, setLocalTableData] = useState([]);

  useEffect(() => {
    setLocalTableData(fetchedData?.data?.data || []);
    // drop selections that disappeared
    setSelectedRowKeys((prev) => {
      if (!prev?.length) return prev;
      const ids = new Set((fetchedData?.data?.data || []).map((x) => x._id));
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [fetchedData]);

  // Active departments
  const { fetchedData: deptFetched = {} } = useFetch(
    `${FETCH_ALL_DEPT_API}/active`,
    state
  );
  const deptList = deptFetched?.data || [];

  // All counters
  const { fetchedData: countersFetched = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    state
  );
  const counters = countersFetched?.data || [];

  // Only counters whose dept is active
  const counterData = useMemo(() => {
    if (!Array.isArray(deptList) || deptList.length === 0) return counters;
    const activeDeptNames = new Set(
      deptList.map((d) => d?.name).filter(Boolean)
    );
    return counters.filter((c) => c?.dept && activeDeptNames.has(c.dept));
  }, [counters, deptList]);

  // Build Select options; ensure ECOMMRI is present when locked
  const uniqueCounterCodes = useMemo(() => {
    const codes = (counterData || [])
      .map((item) => item?.code)
      .filter((v) => typeof v === "string" && v.trim().length > 0);

    let uniq = Array.from(new Set(codes)).sort((a, b) => a.localeCompare(b));

    // If isEcomm and user is ECOMMRI, make sure ECOMMRI is included
    if (
      isEcomm &&
      userData?.counterCode === "ECOMMRI" &&
      !uniq.includes("ECOMMRI")
    ) {
      uniq.unshift("ECOMMRI");
    }

    // If user is not ECOMMRI, remove it from the list
    if (userData?.counterCode !== "ECOMMRI") {
      uniq = uniq.filter((c) => c !== "ECOMMRI");
    }

    return ["All", ...uniq.filter((c) => c !== "All")];
  }, [counterData, isEcomm, userData]);
  

  const paginationInfo = fetchedData?.data?.pagination || {
    current: 1,
    pageSize: 10,
    total: 0,
  };

  // If page becomes empty after removal, navigate smartly
  const navigateIfPageEmpty = (nextPageRowCount) => {
    if (nextPageRowCount > 0) return;
    
    const { current, pageSize } = state;
    const total = paginationInfo?.total ?? 0;
    
    // Calculate total pages
    const totalPages = Math.ceil(total / pageSize);
    
    // If current page is empty, try to navigate to a page with data
    if (current > 1) {
      // Go to previous page if we're not on the first page
      dispatch({ type: "current", payload: current - 1 });
    } else if (current < totalPages) {
      // If we're on first page and it's empty, try next page if available
      dispatch({ type: "current", payload: current + 1 });
    } else {
      // If we're on the last page and it's empty, stay here but refresh
      // This handles the case where all items were removed
    }
    callReFetch();
  };

  const handleRejectSelected = async () => {
    if (selectedRowKeys.length === 0 || rejecting) return;
    setRejecting(true);
    const prevData = [...localTableData];

    setLocalTableData((data) => {
      const next = data.filter((item) => !selectedRowKeys.includes(item._id));
      navigateIfPageEmpty(next.length);
      return next;
    });

    try {
      await rejectStockOpname(selectedRowKeys);
      message.success(
        `Rejected ${selectedRowKeys.length} item(s) successfully.`
      );
      setSelectedRowKeys([]);
    } catch (error) {
      message.error("Failed to reject selected items.");
      setLocalTableData(prevData); // revert
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setRejecting(false);
    }
  };

  const handleCounterCodeChange = (value) => {
    // While locked, ignore user changes and pin to ECOMMRI
    if (isEcomm) {
      setSelectedCounterCode("ECOMMRI");
      dispatch({ type: "current", payload: 1 });
      dispatch({ type: "counterCode", payload: "ECOMMRI" });
      return;
    }

    const code = value === "All" ? null : value;
    setSelectedCounterCode(code);
    dispatch({ type: "current", payload: 1 });
    dispatch({ type: "counterCode", payload: code });
  };

  const handleSearch = (value) => {
    dispatch({ type: "current", payload: 1 });
    dispatch({ type: "searchKeyword", payload: value });
  };

  const handlePaginationChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    dispatch({ type: "current", payload: 1 });
    dispatch({ type: "showNotMatched", payload: checked });
  };

  return (
    <div
      style={{
        width: "100vw",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "80vw",
          maxWidth: "100vw",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Select
          showSearch
          placeholder="Select a Counter Code"
          optionFilterProp="children"
          onChange={handleCounterCodeChange}
          value={isEcomm ? "ECOMMRI" : selectedCounterCode ?? "All"} // stable value
          filterOption={(input, option) =>
            option?.children
              ?.toString()
              ?.toLowerCase()
              ?.includes(input.toLowerCase())
          }
          style={{ width: 200 }}
          disabled={isEcomm} // lock UI when ECOMMRI
        >
          {uniqueCounterCodes.map((code) => (
            <Option key={code} value={code}>
              {code}
            </Option>
          ))}
        </Select>

        <Checkbox
          onChange={handleCheckboxChange}
          checked={state.showNotMatched || false}
        >
          Show Only Not Matched Quantity
        </Checkbox>

        <Search
          placeholder="Search..."
          allowClear
          onSearch={handleSearch}
          style={{ width: 260 }}
        />
      </div>

      <Button onClick={callReFetch} style={{ marginBottom: 12 }}>
        Refresh
      </Button>

      <div
        className="flex gap-3 mb-4 w-full justify-start"
        style={{ width: "80vw", maxWidth: "100vw" }}
      >
        <Button
          danger
          disabled={selectedRowKeys.length === 0}
          loading={rejecting}
          onClick={handleRejectSelected}
        >
          Reject Selected
        </Button>
        <span className="ml-2 text-gray-500">
          {selectedRowKeys.length} item(s) selected
        </span>
      </div>

      <StockOpnameTable
        loading={loading}
        data={localTableData}
        pagination={paginationInfo}
        dispatch={dispatch}
        userData={userData}
        callRefetch={callReFetch}
        updateStockQuantity={updateStockQuantity}
        onPaginationChange={handlePaginationChange}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        onApprove={(id) => {
          setLocalTableData((data) => {
            const next = data.filter((item) => item._id !== id);
            navigateIfPageEmpty(next.length);
            return next;
          });
        }}
        onReject={(id) => {
          setLocalTableData((data) => {
            const next = data.filter((item) => item._id !== id);
            navigateIfPageEmpty(next.length);
            return next;
          });
        }}
      />
    </div>
  );
};

StockOpnamePage.propTypes = {
  history: PropTypes.object,
  userData: PropTypes.object,
};

export default StockOpnamePage;
